import { Box, InputAdornment, styled, TextField, TextFieldProps, Typography } from "@mui/material";
import { AsYouType, CountryCode, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import { useCallback, useMemo, useRef, useState } from "react";
import { CountryCodeSelector } from "./country-code-selector";
import { formatPhoneNumberByCountry, isShortCodeNumber, parsePhoneNumberByCountry } from "@/util/phone-number-utils";
import { isDummyNumber, normalizeNumber } from "./phone-number-bypass";
import { useTranslation } from "react-i18next";

const PhoneNumberInputBox = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexDirection: "column",
}));

const PhoneAdornment = styled(InputAdornment)(({ theme }) => ({
  height: "54px",
  maxHeight: "100%",
  color: "inherit",
  margin: 0,
  padding: "0 14px 0 0",
  borderRight: `1px solid ${theme.palette.divider}`,
}));

export interface PhoneNumberInfo {
  countryCode: string;
  nationalNumber: string;
  e164Number: string;
  isValid: boolean;
}

interface PhoneNumberInputProps extends Omit<TextFieldProps, "onChange" | "defaultValue">{
  defaultValue?: string;
  onChange?: (value: string, info: PhoneNumberInfo) => void;
  label: string;
  disabled?: boolean;
  isError?: boolean;
  showExample?: boolean;
  countryCode?: string;
  countryCodeSelector?: boolean;
  disableShortCode?: boolean;
  allowDummy?: boolean;
}

export const PhoneNumberInput = (props: PhoneNumberInputProps) => {
  return <PhoneNumberInputInner key={props.defaultValue} {...props} />;
};

const PhoneNumberInputInner = ({
  defaultValue = "",
  onChange,
  label,
  disabled,
  isError,
  showExample,
  countryCode,
  countryCodeSelector,
  disableShortCode,
  allowDummy = false, // TEMPORARY BYPASS FLAG
  ...rest
}: PhoneNumberInputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>(countryCode ? (countryCode as CountryCode) : "US");
  const { t } = useTranslation("contacts");

  const formattedDefaultValue = useMemo(() => {
    if (!defaultValue) {
      return "";
    }
    if (isShortCodeNumber(defaultValue)) {
      return defaultValue;
    }
    return formatPhoneNumberByCountry(defaultValue, selectedCountryCode);
  }, [defaultValue, selectedCountryCode]);

  const exampleNumber = useMemo(() => getExampleNumber(selectedCountryCode, examples)?.formatNational() || "", [selectedCountryCode]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      let formattedValue = newValue;

      // TEMPORARY BYPASS SECTION
      if (allowDummy && isDummyNumber(newValue)) {
        const normalized = normalizeNumber(newValue);
        const e164 = normalized.startsWith("+") ? normalized : `+44${normalized}`;

        const asYouType = new AsYouType(selectedCountryCode);
        formattedValue = asYouType.input(normalized);

        if (inputRef.current) {
          inputRef.current.value = formattedValue;
        }

        const info = {
          countryCode: selectedCountryCode,
          nationalNumber: formattedValue,
          e164Number: e164,
          isValid: true,
        };

        onChange?.(info.e164Number, info);
        return;
      }
      // END TEMPORARY BYPASS SECTION

      if (!isShortCodeNumber(newValue) && newValue.length >= 8) {
        const asYouType = new AsYouType(selectedCountryCode);
        formattedValue = asYouType.input(newValue);
      }

      if (inputRef.current) {
        inputRef.current.value = formattedValue;
      }

      if (onChange) {
        const info = parsePhoneNumberByCountry(newValue, selectedCountryCode, disableShortCode);
        onChange(info.e164Number, info);
      }
    },
    [allowDummy, disableShortCode, onChange, selectedCountryCode],
  );

  const handleCountryChange = useCallback(
    (newCountryCode: CountryCode) => {
      setSelectedCountryCode(newCountryCode);
      if (inputRef.current) {
        inputRef.current.value = ""; // Empty the input on country code change
      }

      onChange?.("", parsePhoneNumberByCountry("", newCountryCode, disableShortCode));
      inputRef.current?.focus();
    },
    [disableShortCode, onChange],
  );

  return (
    <PhoneNumberInputBox>
      <TextField
        inputRef={inputRef}
        type="tel"
        fullWidth
        size="medium"
        defaultValue={formattedDefaultValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder={exampleNumber}
        label={label}
        className="contact-number"
        color={isError ? "error" : "primary"}
        {...rest}
        slotProps={{
          input: countryCodeSelector
            ? {
                startAdornment: (
                  <PhoneAdornment position="start">
                    <CountryCodeSelector countryCode={selectedCountryCode} onSelect={handleCountryChange} />
                  </PhoneAdornment>
                ),
              }
            : undefined,

          htmlInput: { "data-cy": "contactNumberInput" }
        }} />
      {showExample && exampleNumber ? <Typography variant="caption">{t("forExample")} {exampleNumber}</Typography> : null}
    </PhoneNumberInputBox>
  );
};
